
import { defineComponent, ref, computed, reactive, watch, nextTick, PropType, onMounted, onUnmounted } from 'vue'
import starApi from '@/api/starApi'

const ASYNC_TYPES = new Set([5, 6, 7])
const MULTI_TYPES = new Set([3, 4, 5, 6])

export default defineComponent({
  name: 'FactorItem',
  props: {
    factor: { type: Object as PropType<any>, required: true },
    selected: Boolean,
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true },
    headers: { type: Object as PropType<Record<string, string>>, default: () => ({}) },
    highlight: Boolean,
    subLabel: { type: String, default: '' },
    showSubLabel: { type: Boolean, default: false }
  },
  emits: ['toggle'],
  setup(props, { emit }) {
    const showPopup = ref(false)
    const popupPos = reactive({ x: 0, y: 0 })
    const loadingOptions = ref(false)
    const asyncOptions = ref<any[]>([])
    const rawSortedValues = ref<number[]>([])  // 原始排序值，用于精确计算默认区间
    const pendingOptions = ref<any[]>([])
    const unionMode = ref<'union' | 'intersect'>('union')
    const searchKw = ref('')
    const showDescTip = ref(false)
    const descTipHover = ref(false)
    const descTipPos = reactive({ x: 0, y: 0 })

    // 区间类型
    const rangeLeft = ref('')
    const rangeRight = ref('')
    const rangeError = ref('')
    const rangeStockCount = ref(0)
    const rangeStockPct = ref('0.00')
    // 内部记忆已确认的区间，不依赖 props.factor.selectedRange
    const savedRange = ref<{ left: string; right: string } | null>(null)
    const chartCanvas = ref<HTMLCanvasElement | null>(null)
    const defaultLeft = ref('')
    const defaultRight = ref('')

    const isMulti = computed(() => MULTI_TYPES.has(props.factor.attrType))
    const hasUnionSwitch = computed(() => [4, 6].includes(props.factor.attrType))

    const optionList = computed(() => {
      if (ASYNC_TYPES.has(props.factor.attrType)) return asyncOptions.value
      return props.factor.childAttrList || []
    })

    const filteredOptions = computed(() => {
      if (!searchKw.value.trim()) return optionList.value
      const kw = searchKw.value.trim().toLowerCase()
      return optionList.value.filter((o: any) => o.attrName?.toLowerCase().includes(kw))
    })

    const pendingCodes = computed(() => new Set(pendingOptions.value.map((o: any) => o.attrCode)))

    const optionListWithDesc = computed(() => optionList.value.filter((o: any) => o.attrDesc))
    const hasOptionDescs = computed(() => optionListWithDesc.value.length > 0)

    const isRangeChanged = computed(() =>
      rangeLeft.value !== defaultLeft.value || rangeRight.value !== defaultRight.value
    )

    // 计算默认区间值，完全对齐客户端 CFactorRangeAlgorithm::CalcRange 逻辑
    // 客户端返回 valueVec[nLeft..nRight)，左右边界取子数组首尾元素
    const calcDefaultRange = (factor: any) => {
      const sorted: number[] = factor.distribution || []
      if (!sorted.length) return { left: '', right: '' }

      const nSize = sorted.length
      const round2 = (v: number) => Math.round(v * 100) / 100
      // defaultRangeType 文档编号从0开始，C++ 枚举从1开始，需要+1对齐
      const type = (factor.defaultRangeType != null ? factor.defaultRangeType + 1 : 1)

      // C++ size_t 乘法截断 = Math.trunc（等同 Math.floor，因为结果为正）
      const trunc = (v: number) => Math.trunc(v)

      let nLeft = 0, nRight = 0

      switch (type) {
        case 1: // 中位数前后各取15%（第35~65分位）
          nLeft  = trunc(nSize * 0.35)
          nRight = trunc(nSize * 0.65)
          break
        case 2: // 中位数前后各取25%（第25~75分位）
          nLeft  = trunc(nSize * 0.25)
          nRight = trunc(nSize * 0.75)
          break
        case 3: { // ≤0 的数据，取中位数前100%
          let idx = -1
          for (let i = nSize - 1; i >= 0; i--) { if (sorted[i] <= 0) { idx = i; break } }
          if (idx < 0) return { left: '', right: '' }
          nLeft = 0
          nRight = nSize - (nSize - 1 - idx)  // = idx + 1
          break
        }
        case 4: // 取中位数后100%
          nLeft  = trunc(nSize * 0.5)
          nRight = trunc(nSize * 1)  // = nSize
          break
        case 5: { // ≥0 的数据全部
          const idx = sorted.findIndex((v: number) => v >= 0)
          if (idx < 0) return { left: '', right: '' }
          nLeft = idx
          nRight = nSize
          break
        }
        case 6: { // ≥0 数据里，取中位数后100%
          const nBegin = sorted.findIndex((v: number) => v >= 0)
          if (nBegin < 0) return { left: '', right: '' }
          const nMid = nBegin + Math.trunc((nSize - 1 - nBegin) / 2)
          nLeft = nMid
          nRight = nSize
          break
        }
        case 7: { // ≥0 数据里，取中位数前100%
          const nBegin = sorted.findIndex((v: number) => v >= 0)
          if (nBegin < 0) return { left: '', right: '' }
          const nMid = nBegin + Math.trunc((nSize - 1 - nBegin) / 2)
          nLeft = 0
          nRight = Math.trunc(nMid / 2)
          break
        }
        default:
          return { left: '', right: '' }
      }

      // 对齐 C++：nLeft <= nRight 才有效（注意客户端是 <=，不是 <）
      if (nLeft > nRight || nRight > nSize) return { left: '', right: '' }
      // 子数组 [nLeft, nRight)，取首尾
      const result = {
        left:  String(round2(sorted[nLeft])),
        right: String(round2(sorted[nRight - 1]))
      }
      console.log('[calcDefaultRange] type:', type, 'nSize:', nSize, 'nLeft:', nLeft, 'nRight:', nRight, 'result:', result)
      return result
    }

    const loadAsyncOptions = async () => {
      if (!ASYNC_TYPES.has(props.factor.attrType)) return
      // attrType 7：每次打开都重新计算默认区间（数据已缓存则跳过网络请求）
      const alreadyLoaded = props.factor.attrType === 7
        ? rawSortedValues.value.length > 0
        : asyncOptions.value.length > 0
      if (alreadyLoaded && props.factor.attrType !== 7) return
      loadingOptions.value = true
      try {
        let raw: any[] = []
        if (!alreadyLoaded) {
          const res = await starApi.getStockFactorOptions({
            data: { factorCode: props.factor.attrCode },
            headers: props.headers
          })
          raw = res?.data || []
        }

        if (props.factor.attrType === 7) {
          // 接口返回的是每只股票的因子值 { symbol, value }
          if (!alreadyLoaded) {
            const values = raw.map((d: any) => d.value ?? 0).filter((v: number) => isFinite(v))
            if (values.length) {
              rawSortedValues.value = [...values].sort((a, b) => a - b)
              console.log('[FactorItem] rawSortedValues count:', rawSortedValues.value.length, 'min:', rawSortedValues.value[0], 'max:', rawSortedValues.value[rawSortedValues.value.length - 1])
            }
          }
          // 每次打开都重新计算默认区间
          console.log('[FactorItem] factor.defaultRangeType:', props.factor.defaultRangeType, 'factor keys:', Object.keys(props.factor))
          const def = calcDefaultRange({ ...props.factor, distribution: rawSortedValues.value })
          defaultLeft.value = def.left
          defaultRight.value = def.right
          // 优先用内部记忆的已选区间，其次 props，最后才用默认值
          const hasSelected = savedRange.value !== null
          if (hasSelected) {
            rangeLeft.value = savedRange.value!.left
            rangeRight.value = savedRange.value!.right
          } else {
            rangeLeft.value = def.left
            rangeRight.value = def.right
          }
          // 有已选区间的情况由 onClick 负责赋值，这里不覆盖
          await nextTick()
          onRangeInput()
          drawChart()
        } else {
          // attrType 5/6：选项列表，映射字段
          asyncOptions.value = raw.map((o: any) => ({
            ...o,
            attrCode: o.optionCode ?? o.attrCode,
            attrName: o.optionName ?? o.attrName,
          }))
        }
      } catch (e) { /* 静默 */ }
      finally { loadingOptions.value = false }
    }

    // 分布图绘制（直接用原始数据，每个值按位置画竖线，不分桶）
    const drawChart = () => {
      const canvas = chartCanvas.value
      if (!canvas) return
      const sorted = rawSortedValues.value
      if (!sorted.length) return
      const W = canvas.offsetWidth || 320
      const H = 60
      canvas.width = W
      canvas.height = H
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const minVal = sorted[0]
      const maxVal = sorted[sorted.length - 1]
      const range = maxVal - minVal || 1

      const lv = rangeLeft.value !== '' ? Number(rangeLeft.value) : -Infinity
      const rv = rangeRight.value !== '' ? Number(rangeRight.value) : Infinity

      // 先统计每个像素列有多少个值，用于高度归一化
      const colCounts = new Array(W).fill(0)
      for (const v of sorted) {
        const col = Math.min(Math.floor(((v - minVal) / range) * W), W - 1)
        colCounts[col]++
      }
      const maxCount = Math.max(...colCounts, 1)

      ctx.clearRect(0, 0, W, H)
      for (let col = 0; col < W; col++) {
        if (colCounts[col] === 0) continue
        const bucketVal = minVal + (col / W) * range
        const inRange = bucketVal >= lv && bucketVal <= rv
        const barH = Math.max(2, (colCounts[col] / maxCount) * (H - 4))
        ctx.fillStyle = inRange
          ? '#e8392a'
          : '#ddd'
        ctx.fillRect(col, H - barH, 1, barH)
      }

      // x 轴底部实线
      ctx.strokeStyle = '#ccc'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(0, H - 1)
      ctx.lineTo(W, H - 1)
      ctx.stroke()
    }

    const onRangeInput = () => {
      const l = rangeLeft.value !== '' ? Number(rangeLeft.value) : null
      const r = rangeRight.value !== '' ? Number(rangeRight.value) : null
      if (l !== null && r !== null && l > r) {
        rangeError.value = '左值必须≤右值'
      } else {
        rangeError.value = ''
      }
      // 用原始排序数组精确统计区间内股票数
      const raw = rawSortedValues.value
      if (raw.length) {
        const total = raw.length
        const inRange = raw.filter(v =>
          (l === null || v >= l) && (r === null || v <= r)
        ).length
        rangeStockCount.value = inRange
        rangeStockPct.value = total ? ((inRange / total) * 100).toFixed(2) : '0.00'
      }
      drawChart()
    }

    const restoreDefault = () => {
      rangeLeft.value = defaultLeft.value
      rangeRight.value = defaultRight.value
      rangeError.value = ''
      onRangeInput()
    }

    // 拖拽分布图调整区间
    const onChartDrag = (e: MouseEvent) => {
      const canvas = chartCanvas.value
      if (!canvas) return
      const sorted = rawSortedValues.value
      if (!sorted.length) return
      const rect = canvas.getBoundingClientRect()
      const minVal = sorted[0]
      const maxVal = sorted[sorted.length - 1]
      const range = maxVal - minVal || 1
      const pct = (e.clientX - rect.left) / rect.width
      const val = Math.round((minVal + pct * range) * 100) / 100
      const lv = rangeLeft.value !== '' ? Number(rangeLeft.value) : minVal
      const rv = rangeRight.value !== '' ? Number(rangeRight.value) : maxVal
      const midPct = ((lv + rv) / 2 - minVal) / range
      if (pct < midPct) rangeLeft.value = String(val)
      else rangeRight.value = String(val)
      onRangeInput()
    }

    // 坐标轴 min/max 直接取原始数据
    const distMin = computed(() => {
      const s = rawSortedValues.value
      if (!s.length) return ''
      return String(Math.round(s[0] * 100) / 100)
    })
    const distMax = computed(() => {
      const s = rawSortedValues.value
      if (!s.length) return ''
      return String(Math.round(s[s.length - 1] * 100) / 100)
    })

    // 滑块手柄位置
    const leftHandlePos = computed(() => {
      const s = rawSortedValues.value
      if (!s.length) return 0
      const minVal = s[0], maxVal = s[s.length - 1]
      const range = maxVal - minVal || 1
      const lv = rangeLeft.value !== '' ? Number(rangeLeft.value) : minVal
      return Math.max(0, Math.min(100, ((lv - minVal) / range) * 100))
    })

    const rightHandlePos = computed(() => {
      const s = rawSortedValues.value
      if (!s.length) return 100
      const minVal = s[0], maxVal = s[s.length - 1]
      const range = maxVal - minVal || 1
      const rv = rangeRight.value !== '' ? Number(rangeRight.value) : maxVal
      return Math.max(0, Math.min(100, ((rv - minVal) / range) * 100))
    })

    // 拖拽滑块手柄
    const onHandleDrag = (side: 'left' | 'right', e: MouseEvent) => {
      e.preventDefault()
      const canvas = chartCanvas.value
      if (!canvas) return
      const sorted = rawSortedValues.value
      if (!sorted.length) return
      const minVal = sorted[0], maxVal = sorted[sorted.length - 1]
      const range = maxVal - minVal || 1

      const onMove = (me: MouseEvent) => {
        const rect = canvas.getBoundingClientRect()
        const pct = Math.max(0, Math.min(1, (me.clientX - rect.left) / rect.width))
        const val = Math.round((minVal + pct * range) * 100) / 100
        if (side === 'left') rangeLeft.value = String(val)
        else rangeRight.value = String(val)
        onRangeInput()
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    }

    // 选中内容回显文字（优先用内部计算，没有则用外部传入的 subLabel）
    const displaySubLabel = computed(() => {
      if (!props.selected) return props.subLabel
      const f = props.factor
      const opts: any[] = f.selectedOptions || []
      const sep = f.unionMode === 'intersect' ? ' 且 ' : ' 或 '
      const range = f.selectedRange
      switch (f.attrType) {
        case 2: return opts.length ? opts[0].attrName : ''
        case 3: return opts.length ? opts.map((o: any) => o.attrName).join(' 或 ') : ''
        case 4: return opts.length ? opts.map((o: any) => o.attrName).join(sep) : ''
        case 5: return opts.length ? opts.map((o: any) => o.attrName).join(' 或 ') : ''
        case 6: return opts.length ? opts.map((o: any) => o.attrName).join(sep) : ''
        case 7: {
          const r = savedRange.value || f.selectedRange
          if (r?.left && r?.right) return `${r.left} — ${r.right}`
          if (r?.left) return `≥ ${r.left}`
          if (r?.right) return `≤ ${r.right}`
          return ''
        }
        default: return props.subLabel
      }
    })

    const onClick = (e: MouseEvent) => {
      e.stopPropagation()
      // 类型1：固定选项，直接 toggle 不弹框
      // attrType 为 null 且无子选项：也直接 toggle，弹框没有内容
      if (props.factor.attrType === 1 || (props.factor.attrType == null && !props.factor.childAttrList?.length)) {
        emit('toggle', props.factor)
        return
      }
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const popupW = [5, 6].includes(props.factor.attrType) ? 320 : 280
      // 弹框预估高度：type7(区间)最高，其他按选项数估算，最大取400
      const estimatedH = props.factor.attrType === 7 ? 320
        : [5, 6].includes(props.factor.attrType) ? 360
        : Math.min(400, 80 + ((props.factor.childAttrList?.length || 4) * 40))
      // 底部留出 summary-bar 高度（约130px）+ 弹框间距
      const summaryBarH = 130
      const spaceBelow = window.innerHeight - rect.bottom - summaryBarH
      const spaceAbove = rect.top
      // 左右：优先左对齐，右侧不够则右对齐
      const spaceRight = window.innerWidth - rect.left
      popupPos.x = spaceRight >= popupW + 8
        ? rect.left + window.scrollX
        : Math.max(8, window.innerWidth - popupW - 8) + window.scrollX
      // 上下：下方空间够就向下弹，否则向上弹
      if (spaceBelow >= estimatedH || spaceBelow >= spaceAbove) {
        popupPos.y = rect.bottom + 6 + window.scrollY
      } else {
        popupPos.y = rect.top - estimatedH - 6 + window.scrollY
      }
      // 初始化 pending（编辑模式带入已选）
      pendingOptions.value = props.factor.selectedOptions ? [...props.factor.selectedOptions] : []
      if (props.factor.attrType === 7) {
        rangeLeft.value = savedRange.value?.left ?? props.factor.selectedRange?.left ?? ''
        rangeRight.value = savedRange.value?.right ?? props.factor.selectedRange?.right ?? ''
      }
      showPopup.value = true
      loadAsyncOptions()
    }

    const toggleOption = (opt: any) => {
      if (opt.locked) {
        // 上锁选项：打开营销H5（此处仅占位）
        return
      }
      if ([1, 2].includes(props.factor.attrType)) {
        // 单选
        pendingOptions.value = pendingCodes.value.has(opt.attrCode) ? [] : [opt]
      } else {
        // 多选
        const idx = pendingOptions.value.findIndex((o: any) => o.attrCode === opt.attrCode)
        if (idx >= 0) pendingOptions.value.splice(idx, 1)
        else pendingOptions.value.push(opt)
      }
    }

    const removePending = (opt: any) => {
      pendingOptions.value = pendingOptions.value.filter((o: any) => o.attrCode !== opt.attrCode)
    }

    const onCancel = () => {
      showPopup.value = false
      pendingOptions.value = []
      searchKw.value = ''
    }

    const onConfirm = () => {
      if (props.factor.attrType === 7) {
        if (rangeError.value) return
        // 内部记忆已选区间
        savedRange.value = { left: rangeLeft.value, right: rangeRight.value }
        emit('toggle', {
          ...props.factor,
          selectedRange: savedRange.value,
          selectedOptions: [],
          _confirmed: true  // 标记已确认，防止被误删
        })
      } else if (pendingOptions.value.length > 0) {
        emit('toggle', {
          ...props.factor,
          selectedOptions: [...pendingOptions.value],
          unionMode: unionMode.value
        })
      }
      showPopup.value = false
      searchKw.value = ''
    }

    const optionStyle = (opt: any) => {
      const active = pendingCodes.value.has(opt.attrCode)
      return {
        background: active ? '#FFEDED' : '#f5f5f5',
        color: opt.locked ? '#bbb' : (active ? '#1a1a1a' : '#1a1a1a'),
        borderColor: active ? '#e8392a' : 'transparent',
        cursor: opt.locked ? 'not-allowed' : 'pointer'
      }
    }

    const pctColor = (v: number | undefined) => {
      if (v === undefined || v === null) return '#999'
      if (v > 0) return '#e8392a'
      if (v < 0) return '#00b578'
      return '#999'
    }

    const formatPct = (v: number | undefined) => {
      if (v === undefined || v === null) return ''
      const s = v > 0 ? v.toFixed(2) + '%' : v.toFixed(2) + '%'
      return s
    }

    // 全局点击关闭
    const onDocClick = (e: MouseEvent) => {
      if (showPopup.value) {
        const popup = document.querySelector('.fi-popup')
        if (popup && !popup.contains(e.target as Node)) onCancel()
      }
    }
    onMounted(() => document.addEventListener('click', onDocClick))
    onUnmounted(() => document.removeEventListener('click', onDocClick))

    watch(() => props.factor.attrCode, () => {
      asyncOptions.value = []
      pendingOptions.value = []
    })

    return {
      showPopup, popupPos, loadingOptions, asyncOptions,
      pendingOptions, pendingCodes, unionMode, searchKw,
      showDescTip, optionListWithDesc, hasOptionDescs, descTipPos, descTipHover,
      isMulti, hasUnionSwitch, optionList, filteredOptions,
      rangeLeft, rangeRight, rangeError, rangeStockCount, rangeStockPct,
      chartCanvas, isRangeChanged,
      onClick, toggleOption, removePending, onCancel, onConfirm,
      optionStyle, pctColor, formatPct,
      onRangeInput, restoreDefault, onChartDrag,
      leftHandlePos, rightHandlePos, onHandleDrag,
      distMin, distMax, displaySubLabel
    }
  }
})
